import { useEffect, useState } from 'react';
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, query, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Teams admin: create / list / delete teams.
// A team has a name, optional description, and an ordered list of responder UIDs
// (the order is the ringing order — we'll honour it in the escalation engine).

export default function Teams() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [teams, setTeams] = useState(null);
  const [responders, setResponders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    const teamsCol = collection(db, 'vendors', vendorId, 'teams');
    const usersCol = collection(db, 'vendors', vendorId, 'users');

    const unsubTeams = onSnapshot(
      teamsCol,
      (snap) => setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    const unsubResp = onSnapshot(
      query(usersCol, where('role', 'in', ['responder', 'vendor_admin'])),
      (snap) => setResponders(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => !u.deletedAt),
      ),
      (err) => setError(err.message),
    );
    return () => { unsubTeams(); unsubResp(); };
  }, [vendorId]);

  async function createTeam(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError(null);
    try {
      await addDoc(collection(db, 'vendors', vendorId, 'teams'), {
        name: name.trim(),
        description: '',
        responderUids: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeTeam(teamId) {
    if (!confirm('Delete this team? Existing tickets routed to it will keep their current routing.')) return;
    try {
      await deleteDoc(doc(db, 'vendors', vendorId, 'teams', teamId));
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleResponderInTeam(team, responderUid) {
    const inTeam = (team.responderUids || []).includes(responderUid);
    const next = inTeam
      ? team.responderUids.filter((u) => u !== responderUid)
      : [...(team.responderUids || []), responderUid];
    try {
      await updateDoc(doc(db, 'vendors', vendorId, 'teams', team.id), {
        responderUids: next,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError(err.message);
    }
  }

  async function moveResponder(team, responderUid, direction) {
    const list = [...(team.responderUids || [])];
    const idx = list.indexOf(responderUid);
    if (idx === -1) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= list.length) return;
    [list[idx], list[swap]] = [list[swap], list[idx]];
    try {
      await updateDoc(doc(db, 'vendors', vendorId, 'teams', team.id), {
        responderUids: list,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <h2>Teams</h2>
      <p style={{ color: 'var(--muted)', marginTop: -8 }}>
        Each team handles one or more skill categories. Within a team, the order of responders is
        the order they'll be rung.
      </p>

      <form onSubmit={createTeam} style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input
          type="text" placeholder="New team name (e.g. Refrigeration AU)"
          value={name} onChange={(e) => setName(e.target.value)}
          style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}
        />
        <button type="submit" disabled={busy || !name.trim()} className="btn-primary">
          {busy ? 'Creating…' : 'Add team'}
        </button>
      </form>

      {error && <div className="error">{error}</div>}

      {teams === null && <div className="placeholder">Loading…</div>}
      {teams && teams.length === 0 && (
        <div className="placeholder">No teams yet. Create your first one above.</div>
      )}
      {teams && teams.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              responders={responders}
              onRemove={() => removeTeam(team.id)}
              onToggle={(uid) => toggleResponderInTeam(team, uid)}
              onMove={(uid, dir) => moveResponder(team, uid, dir)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function TeamCard({ team, responders, onRemove, onToggle, onMove }) {
  const ordered = (team.responderUids || [])
    .map((uid) => responders.find((r) => r.id === uid))
    .filter(Boolean);
  const unassigned = responders.filter((r) => !(team.responderUids || []).includes(r.id));

  return (
    <div className="card">
      <div className="card-header">
        <h3>{team.name}</h3>
        <button onClick={onRemove} className="btn-ghost-danger">Delete</button>
      </div>

      <div style={{ marginTop: 12 }}>
        <strong>Ringing order:</strong>
        {ordered.length === 0 ? (
          <div className="placeholder" style={{ marginTop: 8 }}>
            No responders assigned. Add some below.
          </div>
        ) : (
          <ol style={{ marginTop: 8 }}>
            {ordered.map((r, i) => (
              <li key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ flex: 1 }}>{r.fullName} <small style={{ color: 'var(--muted)' }}>({r.email})</small></span>
                <button onClick={() => onMove(r.id, 'up')} disabled={i === 0} className="btn-tiny">↑</button>
                <button onClick={() => onMove(r.id, 'down')} disabled={i === ordered.length - 1} className="btn-tiny">↓</button>
                <button onClick={() => onToggle(r.id)} className="btn-tiny">Remove</button>
              </li>
            ))}
          </ol>
        )}
      </div>

      {unassigned.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <strong>Add to team:</strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {unassigned.map((r) => (
              <button key={r.id} onClick={() => onToggle(r.id)} className="btn-chip">
                + {r.fullName}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
